import { z } from "zod";

export const cashPlanCategory = z.enum(["maas", "sgk", "vergi", "kira", "enerji", "diger"]);
const valuesShape = {
  name: z.string().trim().min(2).max(100), category: cashPlanCategory,
  amount: z.string().regex(/^(?:0|[1-9]\d{0,15})$/u), currency: z.enum(["TL", "USD", "EUR"]),
  direction: z.enum(["incoming", "outgoing"]),
};
export const cashPlanRevisionSchema = z.object({ fromDate: z.iso.date(), ...valuesShape });
export const cashPlanOccurrenceSchema = z.object({
  date: z.iso.date(), dueDate: z.iso.date(), cancelled: z.boolean().optional(), ...valuesShape,
});
export const cashPlanSchema = z.object({
  id: z.string().uuid(), ...valuesShape,
  startDate: z.iso.date(), endDate: z.iso.date().nullable(),
  frequency: z.enum(["once", "weekly", "monthly"]), active: z.boolean(),
  revisions: z.array(cashPlanRevisionSchema).max(200).optional(),
  occurrences: z.array(cashPlanOccurrenceSchema).max(1000).optional(),
  archivedFrom: z.iso.date().optional(),
}).refine(plan => !plan.endDate || plan.endDate >= plan.startDate, {
  message: "Bitiş tarihi başlangıçtan önce olamaz.", path: ["endDate"],
});
export const cashPlanBindingSchema = z.object({
  planId: z.string().uuid(), date: z.iso.date(), sourceSheet: z.string().min(1).max(40),
  sourceRow: z.string().min(1).max(150), amount: z.string().regex(/^[1-9]\d{0,15}$/u),
  observedAt: z.iso.datetime(),
});
export const cashAvailabilitySchema = z.object({
  date: z.iso.date(), currency: z.enum(["TL", "USD", "EUR"]),
  restrictedAmount: z.string().regex(/^(?:0|[1-9]\d{0,15})$/u),
  note: z.string().trim().min(2).max(500), updatedAt: z.iso.datetime().optional(),
  updatedBy: z.string().min(1).max(160).optional(),
});
export const CASH_PLAN_HISTORY_LIMIT = 5000;
const snapshot = z.union([cashPlanSchema, cashPlanBindingSchema, cashAvailabilitySchema]).nullable();
export const cashPlanHistorySchema = z.object({
  id: z.string().uuid(), at: z.iso.datetime(), actor: z.string().min(1).max(160),
  revision: z.number().int().positive(), entityId: z.string().min(1).max(300), planId: z.string().uuid().optional(),
  action: z.enum(["plan-added", "plan-updated", "plan-archived", "binding-added", "binding-removed", "availability-set", "availability-removed"]),
  before: snapshot, after: snapshot,
});
export const cashPlansSchema = z.object({ company: z.string(), revision: z.number().int().nonnegative(),
  updatedAt: z.iso.datetime().optional(),
  plans: z.array(cashPlanSchema).max(100), bindings: z.array(cashPlanBindingSchema).max(1000),
  cashAvailability: z.array(cashAvailabilitySchema).max(100).optional(),
  history: z.array(cashPlanHistorySchema).max(CASH_PLAN_HISTORY_LIMIT).optional(),
});
export type CashPlan = z.infer<typeof cashPlanSchema>;
export type CashPlanOccurrence = z.infer<typeof cashPlanOccurrenceSchema>;
export type CashPlanHistory = z.infer<typeof cashPlanHistorySchema>;
export type CashAvailability = z.infer<typeof cashAvailabilitySchema>;
export type CashPlans = z.infer<typeof cashPlansSchema>;
